import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { InputNumber } from 'shared/ui/InputNumber';
import { Select } from 'shared/ui/Select';
import { TextArea } from 'shared/ui/TextArea';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { showNotification } from 'app/providers/NotificationsProvider';
import { validateLocalizedJSON } from 'shared/utils/helpers/JSONLocalization';
import { DrawerMode } from '../model/types';
import { POSITIVE_INTEGER_REGEX } from 'shared/utils/regex';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { Discount, DiscountData, useCreateDiscountMutation, useUpdateDiscountMutation } from 'entities/Discount';
import { MultipleSelect } from 'shared/ui/MultipleSelect';
import { AccountTypeSetting, LoyaltyUserSetting, PaymentFrequencySetting } from 'entities/RentOption';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { transformEmptyStringToNull } from 'shared/utils/helpers/transformEmptyStringToNull';
import { InfoTag } from 'shared/ui/InfoTag';
import { Drawer } from 'shared/ui/Drawer';
import { DiscountSettingsForm } from './DiscountSettingsForm';
import { UnitSizesForm } from './UnitSizesForm';
import { UnitType } from 'entities/Contract';
import { useDefaultLocalizedDescription } from 'shared/utils/hooks/useDefaultLocalizedDescription';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';

interface DiscountFormProps {
  discount?: Nullable<Discount>;
  mode: Nullable<DrawerMode>;
  handleClose: () => void;
}

export const DiscountForm: FC<DiscountFormProps> = memo((props) => {
  const { discount, mode, handleClose } = props;
  const { t } = useAppTranslation(['contracts', 'common']);

  const [form] = Form.useForm();

  const isForbidden = useCheckPermission(UserPermissions.EDIT_UNITS);
  const defaultLocalizedDescription = useDefaultLocalizedDescription();

  const [createDiscount, { isLoading: isCreationLoading }] = useCreateDiscountMutation();
  const [updateDiscount, { isLoading: isUpdationLoading }] = useUpdateDiscountMutation();
  const { data: warehouses } = useGetAllWarehousesQuery();

  const [discountSettings, setDiscountSettings] = useState<Nullable<string[]>>(null);
  const [unitSizes, setUnitSizes] = useState<Nullable<string[]>>(null);

  const isLoading = isUpdationLoading || isCreationLoading;

  const warehouseOptions = useMemo(() => {
    return (
      warehouses?.map((warehouse) => ({
        label: warehouse.name,
        value: warehouse.warehouseId,
      })) || []
    );
  }, [warehouses]);

  useEffect(() => {
    if (discount) {
      form.setFieldsValue(discount);
    } else {
      form.resetFields();
    }
  }, [form, discount]);

  const handleSubmit = useCallback(
    async (data: DiscountData): Promise<void> => {
      const discountData = {
        ...transformEmptyStringToNull(data),
        warehouseIds: data.warehouseIds?.length ? data.warehouseIds : null,
        contractDurations: data.contractDurations?.length ? data.contractDurations : null,
        discountKeys:
          typeof data.discountKeys === 'string'
            ? data.discountKeys
                .split(',')
                .map((v) => v.toUpperCase().trim())
                .filter(Boolean)
            : data.discountKeys,
      };

      try {
        switch (mode) {
          case DrawerMode.DISCOUNT_CREATE:
            await createDiscount(discountData as Discount).unwrap();
            showNotification('info', t('Success', { ns: 'common' }), t('Discount has been successfully created'));
            break;
          case DrawerMode.DISCOUNT_EDIT:
            if (discount) {
              const updateDiscountDto: Discount = {
                ...discountData,
                discountId: discount.discountId,
              };

              await updateDiscount(updateDiscountDto).unwrap();
              showNotification('info', t('Success', { ns: 'common' }), t('Discount has been successfully updated'));
            }
            break;
        }

        form.resetFields();

        handleClose();
      } catch (error: CustomAny) {
        if (isErrorApiResponse(error)) {
          const errorMessage = Array.isArray(error.data.message)
            ? error.data.message.map((message) => t(message, { ns: 'common' }))
            : t(error.data.message, { ns: 'common' });

          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        } else {
          console.log(error);
        }
      }
    },
    [mode, form, handleClose, createDiscount, t, discount, updateDiscount],
  );

  const loyaltyPeriodOptions = Object.values(LoyaltyUserSetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const accountTypeOptions = Object.values(AccountTypeSetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const periodTypeOptions = Object.values(InvoiceFrequencyType).map((option) => ({
    label: t(option),
    value: option,
  }));

  const paymentFrequencyOptions = Object.values(PaymentFrequencySetting).map((option) => ({
    label: t(option),
    value: option,
  }));

  const unitTypeOptions = useMemo(() => {
    return [
      {
        label: t('All'),
        value: 'All',
      },
      {
        label: t('Box'),
        value: UnitType.BOX,
      },
      {
        label: t('Office'),
        value: UnitType.OFFICE,
      },
      {
        label: t('Insurance'),
        value: UnitType.INSURANCE,
      },
      {
        label: t('Parking place'),
        value: UnitType.PARKING_PLACE,
      },
      {
        label: t('Postal box'),
        value: UnitType.POSTAL_BOX,
      },
      {
        label: t('Renting stuff'),
        value: UnitType.RENTING_STUFF,
      },
      {
        label: t('Deposit'),
        value: UnitType.DEPOSIT,
      },
    ];
  }, [t]);

  const contractDurationOptions = Array.from({ length: 12 }, (_, i) => {
    const value = i + 1;
    return {
      label: value.toString(),
      value,
    };
  });

  const closeDrawer = (): void => {
    setDiscountSettings(null);
    setUnitSizes(null);
  };

  const openDiscountSettings = useCallback((value: string[]): void => {
    setDiscountSettings(value);
  }, []);

  const openUnitSizesDrawer = useCallback((value: string[]): void => {
    setUnitSizes(value);
  }, []);

  return (
    <div className="min-h-full h-max flex">
      <Drawer width={550} open={Boolean(discountSettings || unitSizes)} onClose={closeDrawer}>
        {Boolean(discountSettings) && (
          <DiscountSettingsForm handleClose={closeDrawer} discountSettings={discountSettings} formInstance={form} />
        )}

        {Boolean(unitSizes) && <UnitSizesForm handleClose={closeDrawer} unitSizes={unitSizes} formInstance={form} />}
      </Drawer>
      <div className="flex-1 shadow-left">
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4" disabled={isForbidden}>
          <div className="flex justify-between items-center pt-4 pb-10">
            <div className="font-semibold text-3xl">
              {mode === DrawerMode.DISCOUNT_EDIT ? `${t('Edit')} ${discount?.name}` : t('Create discount')}
            </div>
            <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
              {t('Close', { ns: 'common' })}
            </div>
          </div>
          <Form.Item label={t('Discount name')} name="name" rules={[{ required: true, message: `${t('Please, enter discount name')}!` }]}>
            <Input placeholder={t('Please, enter discount name')} bordered />
          </Form.Item>
          <Form.Item label={t('Warehouse')} name="warehouseIds">
            <MultipleSelect className="w-full" placeholder={t('All')} bordered options={warehouseOptions} allowClear />
          </Form.Item>
          <Form.Item name="discountKeys" rules={[{ required: true, message: `${t('Please, enter unique discount key')}!` }]}>
            <Input
              placeholder={t('Please, enter unique discount key')}
              bordered
              tooltip={t('The discount key also serves as a promo code and must be unique.')}
              label={t('Discount key')}
              uppercase
            />
          </Form.Item>

          <Form.Item name="discounts" rules={[{ required: true, message: `${t('Please, enter discount settings')}!` }]}>
            <Input
              placeholder={t('Please, enter discount settings')}
              bordered
              tooltip={t(
                'Specify the period and discount amount (% or a fixed value). Example: 1-20%,2-20%,3-20% — applies a 20% discount for the first 3 months.',
              )}
              label={t('Discount settings')}
              onTouchStart={openDiscountSettings}
              readOnly
            />
          </Form.Item>

          <div className="flex gap-4">
            <Form.Item
              label={t('Unit type')}
              name="unitType"
              rules={[{ required: true, message: `${t('Please, select unit type')}!` }]}
              className="flex-1"
            >
              <Select options={unitTypeOptions} placeholder={t('Select unit type')} bordered />
            </Form.Item>

            <Form.Item name="unitName" className="flex-1">
              <Input
                placeholder={t('Please, enter unit name')}
                bordered
                label={t('Unit name')}
                tooltip={t('Enter a unit name if you want to create a discount for a specific unit!')}
              />
            </Form.Item>
          </div>

          <Form.Item name="unitSizes" label={t('Unit sizes')} rules={[{ required: true, message: `${t('Please, select unit sizes')}!` }]}>
            <TextArea placeholder={t('Please, select unit sizes')} bordered onTouchStart={openUnitSizesDrawer} readOnly rows={4} />
          </Form.Item>

          <Divider className="mb-6 mt-0" />

          <div className="flex gap-4">
            <Form.Item
              name="loyaltyPeriod"
              label={t('Client loyalty')}
              rules={[{ required: true, message: `${t('Please, select client loyalty')}!` }]}
              className="flex-1"
            >
              <Select options={loyaltyPeriodOptions} placeholder={t('Select client loyalty')} bordered />
            </Form.Item>

            <Form.Item
              name="accountType"
              label={t('Account type')}
              rules={[{ required: true, message: `${t('Please, select account type')}!` }]}
              className="flex-1"
            >
              <Select options={accountTypeOptions} placeholder={t('Select account type')} bordered />
            </Form.Item>
          </div>

          <div className="flex gap-4">
            <Form.Item
              name="periodType"
              label={t('Period type')}
              rules={[{ required: true, message: `${t('Please, select period type')}!` }]}
              className="flex-1 w-1/2"
            >
              <Select options={periodTypeOptions} placeholder={t('Select period type')} bordered />
            </Form.Item>

            <Form.Item
              name="paymentFrequency"
              label={t('Payment frequency')}
              rules={[{ required: true, message: `${t('Please, select payment frequency')}!` }]}
              className="flex-1 w-1/2"
            >
              <Select options={paymentFrequencyOptions} placeholder={t('Payment frequency')} bordered />
            </Form.Item>
          </div>
          <Form.Item
            name="priority"
            rules={[
              { required: true, message: `${t('Please, enter priority')}!` },
              { pattern: POSITIVE_INTEGER_REGEX, message: t('Please provide positive integer!') },
            ]}
          >
            <InputNumber
              placeholder={t('Enter priority')}
              bordered
              tooltip={t('Discounts with the lowest priority will be applied first!')}
              label={t('Priority')}
            />
          </Form.Item>

          <InfoTag>
            {t(
              'If you want the discount to apply regardless of the contract duration, leave the field empty. Otherwise, specify the duration!',
            )}
          </InfoTag>
          <Form.Item name="contractDurations" label={t('Contract duration')}>
            <MultipleSelect className="w-full" placeholder={t('All')} bordered options={contractDurationOptions} allowClear />
          </Form.Item>

          <Divider className="mb-6 mt-0" />
          <Form.Item
            label={t('Description')}
            name="description"
            rules={[
              () => ({
                async validator(_, value) {
                  const isValidDescription = validateLocalizedJSON(value);

                  if (!value || isValidDescription) {
                    await Promise.resolve();
                    return;
                  }
                  return await Promise.reject(new Error(t('Description must be a valid localized JSON')));
                },
              }),
            ]}
          >
            <TextArea placeholder={t('Add description...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
          </Form.Item>

          <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
            {t('Save', { ns: 'common' })}
          </Button>
        </Form>
      </div>
    </div>
  );
});
